import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { TosModal } from '@/components/TosModal';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SESSION_TOKEN_KEY = 'siyak_access_token';

const readSessionToken = () => {
  try {
    return sessionStorage.getItem(SESSION_TOKEN_KEY);
  } catch (error) {
    return null;
  }
};

const persistSessionToken = (value) => {
  try {
    sessionStorage.setItem(SESSION_TOKEN_KEY, value);
  } catch (error) {
    // Ignore storage edge-case failures and continue with in-memory auth only.
  }
};

const clearSessionToken = () => {
  try {
    sessionStorage.removeItem(SESSION_TOKEN_KEY);
  } catch (error) {
    // Ignore storage edge-case failures.
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  // Keep token only in memory for this tab/session.
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  // True once the user has accepted ToS in this session (avoids re-firing).
  const [tosAccepted, setTosAccepted] = useState(false);
  const [tosData, setTosData] = useState(null);
  const [tosLoading, setTosLoading] = useState(true);
  const authMeInFlightRef = useRef(null);
  const logoutInFlightRef = useRef(false);
  const authMeRequestSeqRef = useRef(0);

  const logout = useCallback(async () => {
    if (logoutInFlightRef.current) {
      return;
    }

    logoutInFlightRef.current = true;
    try {
      // Call backend to clear the HTTP-only cookie
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear in-memory state regardless of backend response
      authMeRequestSeqRef.current += 1;
      delete axios.defaults.headers.common.Authorization;
      clearSessionToken();
      setToken(null);
      setUser(null);
      setLoading(false);
      setSessionExpired(false);
      setTosAccepted(false);
      authMeInFlightRef.current = null;
      logoutInFlightRef.current = false;
    }
  }, []);

  // JWT expiration interceptor
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          const detail = error.response?.data?.detail || '';
          if ((detail === 'Token expired' || detail === 'Invalid token') && !logoutInFlightRef.current) {
            logout();
            setSessionExpired(true);
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [logout]);

  const fetchUser = useCallback(async (force = false) => {
    if (!force && authMeInFlightRef.current) {
      return authMeInFlightRef.current;
    }

    const requestSeq = ++authMeRequestSeqRef.current;

    const request = (async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`);
      if (requestSeq === authMeRequestSeqRef.current) {
        setUser(response.data);
      }
      return response.data;
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401) {
        // Normal unauthenticated state on app load; do not call logout endpoint again.
        if (requestSeq === authMeRequestSeqRef.current) {
          delete axios.defaults.headers.common.Authorization;
          clearSessionToken();
          setToken(null);
          setUser(null);
        }
        return null;
      } else {
        console.error('Failed to fetch user:', error);
      }
      return null;
    } finally {
      if (requestSeq === authMeRequestSeqRef.current) {
        setLoading(false);
      }
      if (authMeInFlightRef.current === request) {
        authMeInFlightRef.current = null;
      }
    }
    })();

    authMeInFlightRef.current = request;
    return request;
  }, []);

  const fetchPublicTos = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/public/tos`);
      setTosData(response.data);
    } catch (error) {
      console.error('Failed to fetch public ToS:', error);
      setTosData(null);
    } finally {
      setTosLoading(false);
    }
  }, []);

  useEffect(() => {
    // Ensure requests include credentials (HTTP-only cookie)
    axios.defaults.withCredentials = true;

    // Restore in-memory auth header from session storage on refresh.
    const persistedToken = readSessionToken();
    if (persistedToken) {
      axios.defaults.headers.common.Authorization = `Bearer ${persistedToken}`;
      setToken(persistedToken);
    } else {
      delete axios.defaults.headers.common.Authorization;
    }

    // Attempt to fetch current user (backend reads cookie)
    fetchUser();
    fetchPublicTos();
  }, [fetchPublicTos, fetchUser]);

  const login = async (email, password) => {
    setSessionExpired(false);
    const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    const { user: userData, access_token: accessToken } = response.data;

    if (accessToken) {
      axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
      persistSessionToken(accessToken);
      setToken(accessToken);
    } else {
      delete axios.defaults.headers.common.Authorization;
      clearSessionToken();
      setToken(null);
    }

    setUser(userData);
    
    // Fetch complete user data (including tenancy_id) from /auth/me
    const refreshedUser = await fetchUser(true);
    
    return refreshedUser || userData;
  };

  const register = async (name, email, password, role, inviteToken) => {
    setSessionExpired(false);
    const response = await axios.post(`${API_URL}/api/auth/register`, { name, email, password, role, invite_token: inviteToken || null });

    // Email verification required — account created but not yet active
    if (response.data?.requires_email_verification) {
      return { requires_email_verification: true, email: response.data.email };
    }

    const { user: userData, access_token: accessToken } = response.data;

    if (accessToken) {
      axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
      persistSessionToken(accessToken);
      setToken(accessToken);
    } else {
      delete axios.defaults.headers.common.Authorization;
      clearSessionToken();
      setToken(null);
    }

    setUser(userData);

    // Fetch complete user data (including tenancy_id for tenants) from /auth/me
    const refreshedUser = await fetchUser(true);

    return refreshedUser || userData;
  };

  const refreshUser = useCallback(async (force = false) => {
    return await fetchUser(force);
  }, [fetchUser]);

  const clearSessionExpired = () => setSessionExpired(false);

  // Called by TosModal after a successful PATCH /api/users/accept-tos response.
  // Set tosAccepted immediately so the modal closes, then re-fetch from /auth/me
  // to ensure user state reflects the DB update (guards against concurrent stale fetches).
  const handleTosAccepted = useCallback(async (updatedUser) => {
    setUser(updatedUser);
    setTosAccepted(true);
    await fetchUser(true);
  }, [fetchUser]);

  const isAdmin = () => user?.role === 'admin';
  const isLandlord = () => user?.role === 'landlord';
  const isTenant = () => user?.role === 'tenant';
  const isAuthenticated = () => !!user;

  // Show ToS modal when accepted version does not match required version.
  const requiredTosVersion = user?.tos_required_version;
  const acceptedTosVersion = user?.tos_version;
  const needsTosAcceptance = !!user && !!requiredTosVersion && acceptedTosVersion !== requiredTosVersion;
  // Within the modal, show ownership selector if the landlord has never set an ownership type.
  const needsOwnershipCapture = needsTosAcceptance && user?.role === 'landlord' && !user?.ownership_type;
  // Activation modal is only shown when ToS acceptance is required.
  const showActivationModal = !!user && !loading && needsTosAcceptance && !tosAccepted;

  return (
    <AuthContext.Provider value={{
      user,
      token,
      plan: user?.plan || 'free',
      accountMetrics: user?.metrics || {},
      featureFlags: user?.features || {},
      loading,
      sessionExpired,
      tosAccepted,
      login,
      register,
      logout,
      refreshUser,
      clearSessionExpired,
      isAdmin,
      isLandlord,
      isTenant,
      isAuthenticated
    }}>
      <TosModal
        open={showActivationModal}
        onAccepted={handleTosAccepted}
        tos={tosData}
        loadingTos={tosLoading}
        needsTos={needsTosAcceptance}
        needsOwnership={needsOwnershipCapture}
      />
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
